import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Lock, ArrowLeft } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const SESSION_KEY = "costing_unlocked";

/**
 * Shared access gate for the entire Costing section — Cost Sheet, Cost
 * Sheet Editor, Budget as per Schedule, and Performance as per Dispatch.
 * One password unlocks all four for the current browser session; the
 * prompt reappears when the tab is closed and reopened.
 */
export default function CostingGuard({ children }) {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY) === "1"; }
    catch { return false; }
  });
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Cross-tab / late unlock sync — if another tab unlocks Costing, this
    // one drops the gate too so the user isn't asked twice in one session.
    const onStorage = (e) => {
      if (e.key === SESSION_KEY && e.newValue === "1") setUnlocked(true);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) { setError("Please enter the password"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/settings/costing-password/verify`,
        { password: password.trim() }, { skipPeriod: true });
      if (res.data?.valid) {
        try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {/* ignore */}
        setUnlocked(true);
        setError("");
      } else {
        setError("Wrong password — try again");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (unlocked) return children;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]" data-testid="costing-gate">
      <Card className="p-8 w-full max-w-md border border-[#E2E8F0] rounded-xl shadow-lg">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[#1D2951] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock size={28} weight="bold" className="text-white" />
          </div>
          <h2 className="text-2xl font-black text-[#0F172A]" style={{fontFamily: 'Chivo'}}>Costing Locked</h2>
          <p className="text-sm text-[#64748B] mt-2">Enter the access password to open Costing pages</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Password</Label>
            <Input type="password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter access password"
              className="min-h-[48px] rounded-lg text-center text-lg tracking-widest" autoFocus
              data-testid="costing-pwd-input" />
            {error && <p className="text-sm text-[#EF4444] text-center" data-testid="costing-pwd-error">{error}</p>}
          </div>
          <Button type="submit" disabled={submitting}
            className="w-full bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[48px] rounded-lg font-semibold"
            data-testid="costing-unlock-btn">
            {submitting ? "Verifying…" : "Unlock"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/")}
            className="w-full min-h-[44px] text-[#64748B] border-[#E2E8F0] rounded-lg"
            data-testid="costing-back-btn">
            <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
          </Button>
        </form>
      </Card>
    </div>
  );
}
